import React, { useMemo, ChangeEvent } from 'react';
import { MessageSquare, ClipboardCopy, AlertTriangle } from 'lucide-react';
import { Checkbox, Button, Table, TableColumn } from 'common/components';
import { formatLeadStatus, formatSMSStatus, getLeadStatusStyle, getSMSStatusStyle } from '../../utils';
import { PartyStatusCell } from '../CallsTable/components';
import type { TableFilterOption } from '../CallsTable/CallsTable';
import { SortDirection } from 'types/call.type';
import { SMSSortField } from 'types/sms.type';
import { DateRangeType } from '../../AgenticWorkflowDetail.types';
import styles from '../CallsTable/CallsTable.module.scss';
import { SMSDateCell, SMSStatusCell } from './components';

export type SMSLead = {
    id: string;
    smsId: string;
    conversationId: string;
    smsDate: string;
    smsTime: string;
    status: string;
    crmStatus: string;
    crmId: string;
    name: string;
    number: string;
    messageContent?: string;
    messageDirection: string;
    disposition?: string;
    deliveredTime?: string;
};

export interface SMSTableDateRangeConfig {
    type: DateRangeType;
    onTypeChange: (range: string) => void;
    setDateRange: (dateRange: [Date | null, Date | null]) => void;
}

export interface SMSTableProps {
    data: SMSLead[];
    selectedIds: string[];
    sortField: SMSSortField;
    sortDirection: SortDirection;
    filterLeadStatus: string;
    filterSMSStatus: string;
    partyStages?: string[];
    smsStatusOptions: TableFilterOption[];
    onSortChange: (field: SMSSortField, newSortDirection: SortDirection) => void;
    onFilterLeadStatus: (value: string) => void;
    onFilterSMSStatus: (value: string) => void;
    onSelectAllCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onCheckboxChange: (smsId: string) => void;
    onSmsDetails?: (record: SMSLead) => void;
    dateRangeConfig: SMSTableDateRangeConfig;
    isLoading: boolean;
    hasActiveFilters: boolean;
}

export const SMSTable = ({
    data,
    selectedIds,
    sortField,
    sortDirection,
    filterLeadStatus,
    filterSMSStatus,
    partyStages = [],
    smsStatusOptions,
    onSortChange,
    onFilterLeadStatus,
    onFilterSMSStatus,
    onSelectAllCheckboxChange,
    onCheckboxChange,
    onSmsDetails,
    dateRangeConfig,
    isLoading,
    hasActiveFilters,
}: SMSTableProps) => {
    const partyStageOptions = useMemo<TableFilterOption[]>(
        () => partyStages.map((stage) => ({ value: stage, label: stage })),
        [partyStages]
    );

    const handleCopy = async (e: React.MouseEvent, record: SMSLead) => {
        e.stopPropagation();
        try {
            const text = `SMS ID: ${record.smsId}\nSMS Date: ${record.smsDate}\nSMS Time: ${record.smsTime}\nSMS Status: ${record.status}\nParty Status: ${record.crmStatus}\nCRM ID: ${record.crmId}\nName: ${record.name}\nNumber: ${record.number}`;
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const noDataMessage = hasActiveFilters
        ? 'No matching SMS conversations found.'
        : 'No SMS conversations available for this workflow.';

    const columns: TableColumn<SMSLead>[] = useMemo(
        () => [
            {
                key: 'actions',
                title: (
                    <Checkbox
                        checked={!!data.length && data.every((row) => selectedIds.includes(row.conversationId))}
                        indeterminate={
                            !!data.length &&
                            data.some((row) => selectedIds.includes(row.conversationId)) &&
                            !data.every((row) => selectedIds.includes(row.conversationId))
                        }
                        onChange={onSelectAllCheckboxChange}
                    />
                ),
                width: 40,
                align: 'left',
                render: (_, record) => (
                    <div onClick={(e) => e.stopPropagation()} className={styles.actionsCellContent}>
                        <div className={styles.copyButton}>
                            <Button icon={ClipboardCopy} iconOnly size='sm' onClick={(e) => handleCopy(e, record)} />
                        </div>
                        <Checkbox
                            checked={selectedIds.includes(record.conversationId)}
                            onChange={() => onCheckboxChange(record.conversationId)}
                        />
                    </div>
                ),
            },
            {
                key: 'smsDate',
                title: (
                    <SMSDateCell
                        dateRangeType={dateRangeConfig.type}
                        onSortChange={onSortChange}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        handleDateRangeChange={dateRangeConfig.onTypeChange}
                        setDateRange={dateRangeConfig.setDateRange}
                    />
                ),
                width: 140,
                className: styles.dateCell,
                render: (_, record) => (
                    <>
                        <div>{record.smsDate}</div>
                        <div className={styles.timeText}>
                            <MessageSquare
                                size={14}
                                className={`${styles.callDirectionIcon} ${record.messageDirection === 'INBOUND' ? styles.callInbound : styles.callOutbound}`}
                            />
                            {record.smsTime}
                        </div>
                    </>
                ),
            },
            {
                key: 'status',
                title: (
                    <SMSStatusCell
                        filterSMSStatus={filterSMSStatus}
                        smsStatusOptions={smsStatusOptions}
                        onFilterSMSStatus={onFilterSMSStatus}
                    />
                ),
                width: 120,
                render: (_, record) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {record.status === 'FAILED' && <AlertTriangle size={14} color='var(--error-color)' />}
                        <span style={getSMSStatusStyle(record.status)}>{formatSMSStatus(record.status)}</span>
                    </div>
                ),
            },
            {
                key: 'partyStatus',
                title: (
                    <PartyStatusCell
                        filterLeadStatus={filterLeadStatus}
                        partyStageOptions={partyStageOptions}
                        onFilterLeadStatus={onFilterLeadStatus}
                    />
                ),
                width: 140,
                render: (_, record) => (
                    <span style={getLeadStatusStyle(record.crmStatus)}>{formatLeadStatus(record.crmStatus)}</span>
                ),
            },
            {
                key: 'crmId',
                title: 'CRM ID',
                dataIndex: 'crmId',
                width: 100,
            },
            {
                key: 'name',
                title: 'Name',
                dataIndex: 'name',
                width: 120,
            },
            {
                key: 'number',
                title: 'Number',
                dataIndex: 'number',
                width: 150,
            },
        ],
        [
            data,
            selectedIds,
            sortField,
            sortDirection,
            dateRangeConfig,
            filterLeadStatus,
            filterSMSStatus,
            partyStageOptions,
            smsStatusOptions,
            onSortChange,
            onFilterLeadStatus,
            onFilterSMSStatus,
            onSelectAllCheckboxChange,
            onCheckboxChange,
            onSmsDetails,
        ]
    );

    return (
        <div className={styles.leadsTableHover}>
            <Table<SMSLead>
                data={data}
                columns={columns}
                emptyText={noDataMessage}
                className={styles.leadsTableContainer}
                rowKey={(record) => record.conversationId}
                onRowClick={onSmsDetails ? (record) => onSmsDetails(record) : undefined}
                rowClassName={(record) =>
                    selectedIds.includes(record.conversationId)
                        ? `${styles.copyCallIdRow} ${styles.selectedRow}`
                        : styles.copyCallIdRow
                }
                showHeader
                variant='primary'
                hoverable
                size='medium'
                isLoading={isLoading}
            />
        </div>
    );
};
